"use client";

import {
  Badge,
  Button,
  CloseButton,
  Dialog,
  Flex,
  Heading,
  Input,
  Portal,
  Spinner,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react";
import ConfirmDialog from "@components/common/ConfirmDialog";
import PhoneInput from "@components/common/PhoneInput";
import { ColumnHeader, EmptyRow } from "@components/common/TableHelpers";
import { formatTimestamp } from "helpers/formatTimestamp";
import { isValidPhone } from "lib/phoneUtils";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { LuSave, LuUnlink } from "react-icons/lu";
import { unassignAccessCodeFromEmployeeAction } from "server/actions/accessCode";
import {
  getEmployeeDetailAction,
  updateEmployeeAction,
} from "server/actions/employee";
import { useOrgId } from "hooks/useOrgId";

interface AccessCode {
  id: number;
  pin: string;
  memo: string | null;
  service: string;
  createdAt: Date;
  archivedAt: Date | null;
}

interface EmployeeDetail {
  id: number;
  name: string;
  phoneNumber: string | null;
  email: string | null;
  accessCodes: AccessCode[];
}

const AssignedCodeRow = memo(
  ({
    code,
    unassigningId,
    onUnassign,
  }: {
    code: AccessCode;
    unassigningId: number | null;
    onUnassign: (id: number) => void;
  }) => {
    const onClickUnassign = useCallback(() => {
      onUnassign(code.id);
    }, [onUnassign, code.id]);

    return (
      <Table.Row fontSize="sm">
        <Table.Cell border="none" p={3}>
          <Text fontSize="sm" fontWeight="bold" fontFamily="mono">
            {code.pin}
          </Text>
        </Table.Cell>
        <Table.Cell border="none" p={3}>
          <Badge size="sm">{code.service}</Badge>
        </Table.Cell>
        <Table.Cell border="none" p={3}>
          <Text fontSize="sm" color="text.secondary">
            {code.memo ?? "-"}
          </Text>
        </Table.Cell>
        <Table.Cell border="none" p={3}>
          <Button
            size="2xs"
            variant="subtle"
            colorPalette="red"
            onClick={onClickUnassign}
            disabled={unassigningId === code.id}
          >
            <LuUnlink />
            Unassign
          </Button>
        </Table.Cell>
      </Table.Row>
    );
  },
);

interface EmployeeDetailModalProps {
  open: boolean;
  onClose: () => void;
  employeeId: number | null;
}

const EmployeeDetailModal = ({
  open,
  onClose,
  employeeId,
}: EmployeeDetailModalProps) => {
  const orgId = useOrgId();
  const [employee, setEmployee] = useState<EmployeeDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [unassigningId, setUnassigningId] = useState<number | null>(null);
  const [pendingUnassignId, setPendingUnassignId] = useState<number | null>(null);

  const loadEmployee = useCallback(async (id: number) => {
    setLoading(true);
    const detail = await getEmployeeDetailAction(orgId, id);
    if (detail != null) {
      setEmployee(detail);
      setName(detail.name);
      setPhone(detail.phoneNumber ?? "");
      setEmail(detail.email ?? "");
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    if (open && employeeId != null) {
      loadEmployee(employeeId);
    }
  }, [open, employeeId, loadEmployee]);

  const onChangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setName(e.target.value);
    },
    [],
  );

  const onChangePhone = useCallback((val: string) => {
    setPhone(val);
  }, []);

  const onChangeEmail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
    },
    [],
  );

  const hasChanges = useMemo(() => {
    if (employee == null) {
      return false;
    }
    return (
      name !== employee.name ||
      phone !== (employee.phoneNumber ?? "") ||
      email !== (employee.email ?? "")
    );
  }, [employee, name, phone, email]);

  const phoneInvalid = phone !== "" && !isValidPhone(phone);

  const onClickSave = useCallback(async () => {
    if (employee == null || phoneInvalid) {
      return;
    }
    setSaving(true);
    try {
      await updateEmployeeAction(employee.id, {
        name: name.trim(),
        phoneNumber: phone !== "" ? phone : null,
        email: email.trim() !== "" ? email.trim() : null,
      });
      await loadEmployee(employee.id);
    } finally {
      setSaving(false);
    }
  }, [employee, name, phone, email, phoneInvalid, loadEmployee]);

  const onRequestUnassign = useCallback((accessCodeId: number) => {
    setPendingUnassignId(accessCodeId);
  }, []);

  const onCloseUnassignConfirm = useCallback(() => {
    setPendingUnassignId(null);
  }, []);

  const onConfirmUnassign = useCallback(async () => {
    if (pendingUnassignId == null || employee == null) {
      return;
    }
    setUnassigningId(pendingUnassignId);
    setPendingUnassignId(null);
    try {
      await unassignAccessCodeFromEmployeeAction(pendingUnassignId);
      await loadEmployee(employee.id);
    } finally {
      setUnassigningId(null);
    }
  }, [pendingUnassignId, employee, loadEmployee]);

  const onCloseModal = useCallback(() => {
    setEmployee(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog.Root open={open} onOpenChange={onCloseModal} lazyMount size="md">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bgColor="surface.background" p={6}>
            <Flex direction="column" gap={4}>
              <Dialog.CloseTrigger asChild position="static">
                <CloseButton size="sm" alignSelf="flex-end" />
              </Dialog.CloseTrigger>

              {loading && (
                <Flex justify="center" py={8}>
                  <Spinner size="md" />
                </Flex>
              )}

              {!loading && employee != null && (
                <Flex direction="column" gap={6}>
                  <Heading fontSize="lg">Edit Employee</Heading>

                <Stack gap={3}>
                  <Stack gap={1}>
                    <Text fontSize="xs" color="text.secondary">
                      Name
                    </Text>
                    <Input
                      size="sm"
                      value={name}
                      onChange={onChangeName}
                      borderColor="border.light"
                    />
                  </Stack>
                  <Stack gap={1}>
                    <Text fontSize="xs" color="text.secondary">
                      Phone number
                    </Text>
                    <PhoneInput
                      size="sm"
                      value={phone}
                      onChange={onChangePhone}
                      borderColor="border.light"
                    />
                    {phoneInvalid && (
                      <Text fontSize="xs" color="fg.error">
                        Enter a valid phone number
                      </Text>
                    )}
                  </Stack>
                  <Stack gap={1}>
                    <Text fontSize="xs" color="text.secondary">
                      Email
                    </Text>
                    <Input
                      size="sm"
                      value={email}
                      onChange={onChangeEmail}
                      borderColor="border.light"
                    />
                  </Stack>
                  {hasChanges && (
                    <Flex justify="flex-end">
                      <Button
                        size="sm"
                        onClick={onClickSave}
                        disabled={saving || name.trim().length === 0 || phoneInvalid}
                        loading={saving}
                        loadingText="Saving"
                      >
                        <LuSave />
                        Save Changes
                      </Button>
                    </Flex>
                  )}
                </Stack>

                <Flex direction="column" gap={2}>
                  <Text fontSize="sm" fontWeight="semibold">
                    Assigned PIN Codes
                  </Text>
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row bg="surface.muted">
                          <ColumnHeader>PIN</ColumnHeader>
                          <ColumnHeader>Service</ColumnHeader>
                          <ColumnHeader>Memo</ColumnHeader>
                          <ColumnHeader>Actions</ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {employee.accessCodes.length === 0 && (
                          <EmptyRow colSpan={4}>
                            No PIN codes assigned to this employee.
                          </EmptyRow>
                        )}
                        {employee.accessCodes.map((code) => (
                          <AssignedCodeRow
                            key={code.id}
                            code={code}
                            unassigningId={unassigningId}
                            onUnassign={onRequestUnassign}
                          />
                        ))}
                      </Table.Body>
                  </Table.Root>
                </Flex>
              </Flex>
            )}
            </Flex>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
      <ConfirmDialog
        open={pendingUnassignId != null}
        onClose={onCloseUnassignConfirm}
        onConfirm={onConfirmUnassign}
        title="Unassign PIN code"
        description="Are you sure you want to unassign this PIN code from the employee?"
        confirmLabel="Unassign"
        loading={unassigningId != null}
      />
    </Dialog.Root>
  );
};

export default EmployeeDetailModal;
